// modules/animations.js
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, SplitText);

// --- Scroll header ---
export async function initHeaderScroll(container = document) {
    const header = container.querySelector('header');
    if (!header) return;

    // Check of header initieel 'dark' had
    const hadDarkInitially = header.classList.contains('dark');

    // Verwijder oude listener als die bestaat
    window.removeEventListener('scroll', header._scrollListener);

    const listener = () => {
        if (window.scrollY >= 24) {
            header.classList.add('scrolled');
            header.classList.add('light');
            header.classList.remove('dark');
        } else {
            header.classList.remove('scrolled');
            header.classList.remove('light');

            if (hadDarkInitially) {
                header.classList.add('dark');
            }
        }
    };

    header._scrollListener = listener;
    window.addEventListener('scroll', listener);
    listener(); // direct uitvoeren
}

// --- Accordion ---
function initAccordion(container = document) {
    const faqItems = container.querySelectorAll('.accordion-item');
    faqItems.forEach(item => {
        const btn = item.querySelector('.accordion-header');
        if (!btn) return;

        btn.addEventListener('click', () => {
            faqItems.forEach(i => {
                if (i !== item) i.classList.remove('active');
            });
            item.classList.toggle('active');
        });
    });
}

// --- Form label animations ---
function initFormLabels(container = document) {
    container.querySelectorAll('.fui-field').forEach(field => {
        const input = field.querySelector('input, textarea');
        const label = field.querySelector('label');
        if (!input || !label) return;

        const showLabel = () => {
            label.style.opacity = '100';
        };
        const hideLabelIfFilled = () => {
            if (input.value.trim() !== '') label.style.opacity = '0';
        };
        const resetLabelIfEmpty = () => {
            if (input.value.trim() === '') label.style.opacity = '100';
        };

        input.addEventListener('focus', showLabel);
        input.addEventListener('blur', hideLabelIfFilled);
        input.addEventListener('input', resetLabelIfEmpty);

        if (input.value.trim() !== '') label.style.opacity = '100';
    });
}

// --- Rating stars ---
function initRatingStars(container = document) {
    const scoreBlocks = container.querySelectorAll('.review-score');
    scoreBlocks.forEach(scoreEl => {
        const wrapper = scoreEl.closest('.score');
        if (!wrapper) return;

        const filledStars = wrapper.querySelector('.filled-stars');
        if (!filledStars) return;

        const score = parseFloat(scoreEl.textContent.trim());
        const maxScore = 10;
        const maxStars = 5;

        const stars = Math.floor((score / maxScore) * maxStars * 2) / 2;
        const percent = (stars / maxStars) * 100;

        filledStars.style.clipPath = `inset(0 ${100 - percent}% 0 0)`;
    });
}

// --- Sections animations ---
function initSectionAnimations(container = document) {
    const sectionAnimation = container.querySelector('.animate-in');
    if (!sectionAnimation) return;

    gsap.set('.animate-in .fade-in', { opacity: 0, y: 20 });

    ScrollTrigger.batch('.animate-in .fade-in', {
        onEnter: batch => gsap.to(batch, { opacity: 1, y: 0, stagger: 0.15 }),
        onLeave: batch => gsap.to(batch, { opacity: 0, y: 20 }),
        onEnterBack: batch => gsap.to(batch, { opacity: 1, y: 0, stagger: 0.15 }),
        onLeaveBack: batch => gsap.to(batch, { opacity: 0, y: 20 }),
        start: "top 87%",
        end: "bottom 10%"
    });
}

// --- Split buttons ---
export function initSplitButtons(container = document) {
    const splitButtons = container.querySelectorAll('.split-button');

    splitButtons.forEach(item => {
        let isOpen = false;

        item.addEventListener('click', e => {
            if (e.target.closest('a')) return;
            e.preventDefault();
            isOpen = !isOpen;
            item.classList.toggle('active', isOpen);
        });

        // Sluit de button als je buiten klikt
        document.addEventListener('click', e => {
            if (!item.contains(e.target) && isOpen) {
                item.classList.remove('active');
                isOpen = false;
            }
        });
    });
}

// --- Headings reveal ---
function initHeadings(container = document) {
    container.querySelectorAll('[data-split="heading"]').forEach(heading => {
        document.fonts.ready.then(() => {
            const type = heading.dataset.splitType || "lines";
            const split = new SplitText(heading, {
                type: "lines, words, chars",
                mask: "lines",
                linesClass: "line",
                wordsClass: "word",
                charsClass: "letter"
            });

            const config = {
                lines: { duration: 0.8, stagger: 0.08 },
                words: { duration: 0.6, stagger: 0.06 },
                letters: { duration: 0.4, stagger: 0.008 }
            };

            const className = type === "lines" ? "line" : type === "words" ? "word" : "letter";
            const targets = heading.querySelectorAll(`.${className}`);
            const { duration, stagger } = config[type] || config.lines;

            const tween = gsap.fromTo(
                targets,
                { yPercent: 110, opacity: 0 },
                { yPercent: 0, opacity: 1, duration, stagger, ease: "power2.out" }
            );

            ScrollTrigger.create({
                trigger: heading,
                start: "top 90%",
                animation: tween,
                toggleActions: "play none none reverse"
            });
        });
    });
}

// --- Copy link button ---
function initCopyLink(container = document) {
    const shareButton = container.querySelector('#share-button');
    if (!shareButton) return;

    let timeoutHandler = null;
    shareButton.addEventListener('click', () => {
        navigator.clipboard.writeText(window.location.href);
        shareButton.classList.add('share-button--copied');

        if (timeoutHandler) clearTimeout(timeoutHandler);
        timeoutHandler = setTimeout(() => {
            shareButton.classList.remove('share-button--copied');
        }, 2000);
    });
}

// --- Init all animations ---
export async function initAnimations(container = document) {
    initSectionAnimations(container);
    initHeadings(container);
    initAccordion(container);
    initFormLabels(container);
    initRatingStars(container);
    initCopyLink(container);
    initSplitButtons(container);
}
